/**
 * Shared in-memory store for the iconik launch credentials (email + password).
 *
 * Backs both the manager's own sign-in (RequestPinScreen, PR #211) and the pre-existing
 * "Open in Iconik" cross-org launch feature (SelectOrganization) — a single successful login
 * populates both, so a user only enters their iconik password once per browser session.
 *
 * IN-MEMORY ONLY, not sessionStorage (changed 2026-07-29, per Addison: "you can store
 * user/pass, but only in memory. That includes post login."). A plain module-level variable —
 * dies on refresh/tab-close. That's an accepted tradeoff: the user re-enters creds on next
 * launch rather than the browser persisting them anywhere, even session-scoped.
 *
 * Kept in a separate module (not in SelectOrganization.tsx) so that ApiService and
 * RequestPinScreen can both import clearLaunchCreds/saveLaunchCreds without creating a
 * circular dependency:
 *
 *   ApiService.ts          ←  launchCreds.ts  (this file; no imports from ApiService)
 *   RequestPinScreen.tsx   ←  launchCreds.ts
 *   SelectOrganization.tsx ←  launchCreds.ts  (re-exports the helpers)
 */

export interface Creds {
  email: string;
  password: string;
}

interface CachedCreds extends Creds {
  expiresAt: number; // Unix seconds — must be <= the session's own expires field
}

let cache: CachedCreds | null = null;

/**
 * Cache launch creds in memory, bounded by expiresAt (normally the manager session's own
 * `expires`, so cached creds never outlive the session that authorized them). Callers compute
 * expiresAt themselves (via ApiService.getSession()) rather than this module importing
 * ApiService, to avoid the circular dependency noted above.
 */
export const saveLaunchCreds = (creds: Creds, expiresAt: number): void => {
  cache = { ...creds, expiresAt };
};

/**
 * Read back cached creds. Returns null (and evicts) once expiresAt has passed.
 * Does not check manager-session liveness itself — see SelectOrganization.tsx's loadCreds for
 * the wrapper that layers that guard on top.
 */
export const getLaunchCreds = (): Creds | null => {
  if (!cache) {
    return null;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  if (!cache.expiresAt || nowSeconds >= cache.expiresAt) {
    cache = null;
    return null;
  }
  const { email, password } = cache;
  return { email, password };
};

/**
 * Clear cached launch creds. Call this whenever the manager session ends (signOut, 401 auth
 * failure).
 */
export const clearLaunchCreds = (): void => {
  cache = null;
};
